import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { GetMyDistricts, UpdateMyDistricts } from '../../../../services/peerToPeer/PeerToPeerService'
import { type DistrictOption } from '../../../../models/peerToPeer/Collection'
import { handleApiResponse } from '../../../../utils/apiHelpers'
import { toast } from 'react-toastify'
import { P2PPrimaryButton } from '../P2PButtons'
import { stepStyles } from './steps/stepStyles'

interface Props {
  reportingYearId: number
  onSaved?: () => void
}

export const DistrictSelection = ({ reportingYearId, onSaved }: Props): JSX.Element => {
  const [available, setAvailable] = useState<DistrictOption[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  useEffect(() => {
    const load = async (): Promise<void> => {
      setIsLoading(true)
      const result = await handleApiResponse(GetMyDistricts(reportingYearId))
      if (result != null) {
        setAvailable(result.available)
        setSelected(new Set(result.selectedDistrictIds))
      }
      setIsLoading(false)
    }
    void load()
  }, [reportingYearId])

  const toggle = (id: number): void => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const save = async (): Promise<void> => {
    setIsSaving(true)
    // Gate on the success callback, not the response body: the PUT returns an empty
    // (204) body, so the parsed result is null even on success — checking it would
    // strand the user on this screen after a successful save.
    await handleApiResponse(
      UpdateMyDistricts({ reportingYearId, districtOrganizationIds: Array.from(selected) }),
      {
        onSuccess: () => {
          toast.success('Districts updated')
          onSaved?.()
        }
      }
    )
    setIsSaving(false)
  }

  if (isLoading) return <CircularProgress />

  return (
    <Box data-testid='p2pDistrictSelection' sx={{ mt: '16px' }}>
      <Typography variant='h3' mb='8px'>Select your district(s)</Typography>
      <Typography variant='body2' mb='12px'>
        Choose the district(s) within your ISD you report peer-to-peer data for. You can change this at any time.
      </Typography>
      <Box sx={stepStyles.checkList}>
        {available.map(district => (
          <Box key={district.id} sx={stepStyles.checkRow}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selected.has(district.id)}
                  onChange={() => { toggle(district.id) }}
                  data-testid={`p2pDistrictOption-${district.id}`}
                />
              }
              label={district.name}
              slotProps={{ typography: { sx: stepStyles.checkLabel } }}
            />
          </Box>
        ))}
      </Box>
      <P2PPrimaryButton
        onClick={() => { void save() }}
        disabled={isSaving}
        dataTestId='p2pDistrictSelectionSave'
        sx={{ mt: '12px' }}
      >
        Save
      </P2PPrimaryButton>
    </Box>
  )
}
